
import { createClient } from '@supabase/supabase-js'
import { resolveStorageBearer } from '../utils/supabaseStorageAuth'

function readEnvVar(key: string): string | undefined {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
        return import.meta.env[key as keyof ImportMetaEnv] as string | undefined
    }
    const proc = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process
    return proc?.env?.[key]
}

// Support both Vite (browser) and Node.js (worker) environments
const supabaseUrl = readEnvVar('VITE_SUPABASE_URL')
const supabaseAnonKey = readEnvVar('VITE_SUPABASE_ANON_KEY')

// Detect if running in browser or Node.js
const isBrowser = typeof window !== 'undefined'

if (!supabaseUrl || !supabaseAnonKey) {
    // Missing env — client will fail on first request
}

// Browser-specific code (only runs in browser environment)
let tabStorageKey = 'sb-auth-default'
let supabaseGlobalFetch = globalThis.fetch

if (isBrowser) {
    /**
     * Use one stable localStorage key for Supabase Auth across all tabs.
     * Per-tab keys (sessionStorage) break cross-tab impersonation lock.
     * where the new tab would otherwise generate a new key and not see the session.
     */
    const AUTH_STORAGE_KEY_META = 'happometer-sb-auth-storage-key-v1'
    /** Pre-migration: per-tab key name lived in sessionStorage under this name. */
    const LEGACY_TAB_KEY_NAME = 'sb-tab-storage-key'

    /** If the meta key was recreated but the session blob still lives under an older `sb-*` key, find it. */
    function findLocalStorageSupabaseAuthKey(): string | null {
        try {
            for (let i = 0; i < window.localStorage.length; i++) {
                const k = window.localStorage.key(i)
                if (!k || !k.startsWith('sb-')) continue
                const raw = window.localStorage.getItem(k)
                if (!raw || raw.length < 80) continue
                if (raw.includes('"access_token"') && raw.includes('"refresh_token"')) {
                    return k
                }
            }
        } catch {
            /* ignore */
        }
        return null
    }

    const getStableAuthStorageKey = (): string => {
        try {
            const legacyFromTab = window.sessionStorage.getItem(LEGACY_TAB_KEY_NAME)
            const recovered = findLocalStorageSupabaseAuthKey()

            let key = window.localStorage.getItem(AUTH_STORAGE_KEY_META)

            if (!key) {
                key = legacyFromTab || recovered || `sb-auth-${crypto.randomUUID().slice(0, 8)}`
                window.localStorage.setItem(AUTH_STORAGE_KEY_META, key)
            } else {
                const blob = window.localStorage.getItem(key)
                const blobMissing = !blob || blob.length < 80
                if (blobMissing && recovered && recovered !== key) {
                    key = recovered
                    window.localStorage.setItem(AUTH_STORAGE_KEY_META, key)
                }
            }
            return key
        } catch {
            return 'sb-auth-fallback'
        }
    }

    tabStorageKey = getStableAuthStorageKey()

    const nativeFetch = window.fetch.bind(window)

    /** Gate so we do not flood sign-out: one 502/503/504 burst = one event per window. */
    let lastServerDownAt = 0
    function notifyServerUnavailable() {
        const now = Date.now()
        if (now - lastServerDownAt < 2000) return
        lastServerDownAt = now
        try {
            window.dispatchEvent(new Event('app-server-unavailable'))
        } catch { /* ignore */ }
    }

    let lastNetworkOfflineAt = 0
    function notifyNetworkOffline() {
        const now = Date.now()
        if (now - lastNetworkOfflineAt < 2000) return
        lastNetworkOfflineAt = now
        try {
            window.dispatchEvent(new Event('app-network-offline'))
        } catch { /* ignore */ }
    }

    /**
     * Supabase + REST calls: 502/503/504 => maintenance; failed fetch + offline/ network error => offline (separate UI).
     */
    supabaseGlobalFetch = function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
        let requestInit = init
        if (supabaseUrl) {
            const href =
                typeof input === 'string'
                    ? input
                    : input instanceof URL
                      ? input.href
                      : input.url
            if (href.startsWith(supabaseUrl)) {
                // Never inject staff JWT into GoTrue — it causes 403 on /auth/v1/user for tenant sessions.
                const isAuthRoute = href.includes('/auth/v1/')
                const isStaffLoginRoute = href.includes('/functions/v1/staff-login')
                if (!isAuthRoute && !isStaffLoginRoute) {
                    const staffBearer = resolveStorageBearer(supabaseAnonKey || '', tabStorageKey)
                    if (staffBearer) {
                        const headers = new Headers(init?.headers)
                        headers.set('Authorization', `Bearer ${staffBearer}`)
                        requestInit = { ...init, headers }
                    }
                }
            }
        }

        return nativeFetch(input, requestInit)
            .then((res) => {
                if (res.status === 502 || res.status === 503 || res.status === 504) {
                    notifyServerUnavailable()
                }
                return res
            })
            .catch((err) => {
                const offline = typeof navigator !== 'undefined' && !navigator.onLine
                const failedFetch =
                    err instanceof TypeError &&
                    /failed to fetch|networkError|load failed|network request failed/i.test(String(err?.message || ''))
                if (offline || failedFetch) {
                    notifyNetworkOffline()
                }
                return Promise.reject(err)
            })
    }
}

export const supabase = createClient(
    supabaseUrl || '',
    supabaseAnonKey || '',
    {
        global: { fetch: supabaseGlobalFetch },
        auth: isBrowser ? {
            persistSession: true,
            storage: window.localStorage,
            storageKey: tabStorageKey,
            autoRefreshToken: true,
            detectSessionInUrl: true
        } : {
            persistSession: false,
            autoRefreshToken: false,
            detectSessionInUrl: false
        }
    }
)
